import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Observable, concatMap, of } from 'rxjs';
import { RxUtils, Tr } from '@utils';
import { PortalRoutePath, getPortalRoute } from '@models';
import { fleetVehicleService, userService } from '@services';
import { Box, Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { VoltaageModalDialog } from '@components/common';
import { ReactComponent as Illustration } from '@assets/illustrations/need-help.svg';

export const TermsAndConditionsDialog: FC = () => {
	const navigate = useNavigate();
	const navigateToVehicleForm = (): Observable<string | undefined> => {
		return fleetVehicleService
			.getAll()
			.pipe(
				concatMap((vehicles) =>
					of(vehicles[0].id ? `${getPortalRoute(PortalRoutePath.VEHICLE_LIST)}/edit/${vehicles[0].id}` : undefined)
				)
			);
	};

	const accept = () => {
		RxUtils.promisify(
			userService.acceptTerms().pipe(
				concatMap(() => userService.getMe()),
				concatMap((user) => (user.isDriver() ? navigateToVehicleForm() : of(undefined)))
			),
			(url) => url && navigate(url)
		);
	};

	return (
		<VoltaageModalDialog
			title={<Tr.Portal path="common.terms-and-conditions.title" />}
			open
			disablePortal
			hideCloseIcon
			maxWidth="sm"
			disableEscapeKeyDown
			sx={{ '&.MuiDialog-root': { position: 'absolute', margin: 0 } }}
			BackdropProps={{ sx: { '&.MuiBackdrop-root': { position: 'absolute', backdropFilter: 'blur(8px)' } } }}
		>
			<Divider light />
			<Stack direction="column" sx={{ paddingX: 6, paddingY: 4 }} spacing={3}>
				<Box sx={{ marginX: 'auto' }}>
					<Illustration style={{ minWidth: 188 }} />
				</Box>
				<Typography sx={{ paddingTop: 2 }}>
					<Tr.Portal path="common.terms-and-conditions.content" withInsecureTemplating />
				</Typography>
				<Divider light />
				<Box>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<Button fullWidth variant="outlined" size="small" href="https://voltaage.io/terms/" target="_blank">
								<Tr.Portal path="common.terms-and-conditions.terms-link" />
							</Button>
						</Grid>
						<Grid item xs={6}>
							<Button fullWidth variant="outlined" size="small" href="https://voltaage.io/privacy-policy/" target="_blank">
								<Tr.Portal path="common.terms-and-conditions.privacy-policy-link" />
							</Button>
						</Grid>
					</Grid>
				</Box>
				<Divider light />
				<Button variant="contained" onClick={accept}>
					<Tr.Portal path="common.terms-and-conditions.cta" />
				</Button>
			</Stack>
		</VoltaageModalDialog>
	);
};
